import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  makeStyles,
  Spinner,
  Subtitle2,
  Text,
  tokens,
} from "@fluentui/react-components"
import { PersonRegular } from "@fluentui/react-icons"
import type { ChangePasswordResponse, MeResponse, UpdateMeRequest } from "@rema/shared"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router"
import { useSession } from "../../auth/session"
import { EmptyState } from "../../components/empty-state"
import { FormField } from "../../components/form-field"
import { PageHeader } from "../../components/page-header"
import { es } from "../../i18n/es"
import { api, isApiError, refreshSession } from "../../lib/api"
import { applyFieldErrors, problemMessage } from "../../lib/problem"
import { profileI18n } from "./i18n"

const useStyles = makeStyles({
  layout: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXL,
    maxWidth: "560px",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalXXL,
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  sectionHint: {
    color: tokens.colorNeutralForeground3,
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXXL,
  },
})

interface DetailsFormValues {
  name: string
  email: string
}

interface PasswordFormValues {
  current_password: string
  new_password: string
  confirm_password: string
}

const ME_QUERY_KEY = ["me"] as const

export const ProfilePage = () => {
  const styles = useStyles()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { logout } = useSession()

  const meQuery = useQuery<MeResponse>({
    queryKey: ME_QUERY_KEY,
    queryFn: () => api.get<MeResponse>("/me"),
  })

  if (meQuery.isLoading) {
    return (
      <>
        <PageHeader title={profileI18n.title} description={profileI18n.description} />
        <div className={styles.loading}>
          <Spinner label={es.common.loading} />
        </div>
      </>
    )
  }

  if (meQuery.isError || !meQuery.data) {
    return (
      <>
        <PageHeader title={profileI18n.title} description={profileI18n.description} />
        <EmptyState
          icon={<PersonRegular />}
          title={profileI18n.loadError}
          description={
            isApiError(meQuery.error) ? problemMessage(meQuery.error.problem) : es.common.loadError
          }
          action={<Button onClick={() => void meQuery.refetch()}>{es.common.retry}</Button>}
        />
      </>
    )
  }

  return (
    <>
      <PageHeader title={profileI18n.title} description={profileI18n.description} />
      <div className={styles.layout}>
        <DetailsSection
          me={meQuery.data}
          onUpdated={(updated) => {
            // Refleja el cambio en la cache de /me y, forzando un refresh, en los
            // claims del access token y en la sesión (avatar/nombre del shell).
            queryClient.setQueryData(ME_QUERY_KEY, updated)
            void refreshSession()
          }}
        />
        <PasswordSection
          onChanged={async () => {
            // El cambio revoca todas las sesiones: se cierra la actual y se vuelve al login.
            await logout()
            navigate("/login", { replace: true })
          }}
        />
      </div>
    </>
  )
}

interface DetailsSectionProps {
  me: MeResponse
  onUpdated: (updated: MeResponse) => void
}

const DetailsSection = ({ me, onUpdated }: DetailsSectionProps) => {
  const styles = useStyles()
  const [formError, setFormError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const { control, handleSubmit, setError, reset } = useForm<DetailsFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { name: me.name ?? "", email: me.email ?? "" },
  })

  const mutation = useMutation({
    mutationFn: (values: DetailsFormValues) => {
      const body: UpdateMeRequest = {
        name: values.name.trim(),
        // Email vacío se envía como cadena vacía → el backend lo normaliza a null.
        email: values.email.trim(),
      }
      return api.patch<MeResponse>("/me", body)
    },
    onSuccess: (updated) => {
      setSaved(true)
      reset({ name: updated.name ?? "", email: updated.email ?? "" })
      onUpdated(updated)
    },
    onError: (error) => {
      if (isApiError(error)) {
        const mapped = applyFieldErrors(error.problem, setError)
        if (!mapped) setFormError(problemMessage(error.problem))
      } else {
        setFormError(es.errors.unexpected)
      }
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    setSaved(false)
    mutation.mutate(values)
  })

  return (
    <section className={styles.section} aria-label={profileI18n.details.heading}>
      <Subtitle2 as="h2">{profileI18n.details.heading}</Subtitle2>
      {saved ? (
        <MessageBar intent="success">
          <MessageBarBody>{profileI18n.details.saved}</MessageBarBody>
        </MessageBar>
      ) : null}
      {formError ? (
        <MessageBar intent="error">
          <MessageBarBody>{formError}</MessageBarBody>
        </MessageBar>
      ) : null}
      {/* Identificador y dominio son de solo lectura: el username no es editable. */}
      <Field label={profileI18n.details.identifierLabel} hint={profileI18n.details.identifierHint}>
        <Input value={me.identifier} readOnly disabled />
      </Field>
      <Field label={profileI18n.details.domainLabel}>
        <Input value={me.domain} readOnly disabled />
      </Field>
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <FormField
          control={control}
          name="name"
          label={profileI18n.details.nameLabel}
          rules={{ maxLength: { value: 200, message: profileI18n.validation.nameTooLong } }}
        >
          {(field, invalid) => (
            <Input
              {...field}
              type="text"
              autoComplete="name"
              placeholder={profileI18n.details.namePlaceholder}
              aria-invalid={invalid}
            />
          )}
        </FormField>
        <FormField
          control={control}
          name="email"
          label={profileI18n.details.emailLabel}
          hint={profileI18n.details.emailHint}
          rules={{
            // Email opcional; si se escribe, debe tener formato válido.
            validate: (value) =>
              value.trim().length === 0 ||
              /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim()) ||
              profileI18n.validation.emailFormat,
          }}
        >
          {(field, invalid) => (
            <Input
              {...field}
              type="email"
              autoComplete="email"
              placeholder={profileI18n.details.emailPlaceholder}
              aria-invalid={invalid}
            />
          )}
        </FormField>
        <div className={styles.actions}>
          <Button
            appearance="primary"
            type="submit"
            disabled={mutation.isPending}
            icon={mutation.isPending ? <Spinner size="tiny" /> : undefined}
          >
            {profileI18n.details.save}
          </Button>
        </div>
      </form>
    </section>
  )
}

interface PasswordSectionProps {
  onChanged: () => void | Promise<void>
}

const PasswordSection = ({ onChanged }: PasswordSectionProps) => {
  const styles = useStyles()
  const [formError, setFormError] = useState<string | null>(null)
  const [changed, setChanged] = useState(false)

  const { control, handleSubmit, setError, watch, reset } = useForm<PasswordFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { current_password: "", new_password: "", confirm_password: "" },
  })

  const mutation = useMutation({
    mutationFn: (values: PasswordFormValues) =>
      api.post<ChangePasswordResponse>("/me/password", {
        current_password: values.current_password,
        new_password: values.new_password,
      }),
    onSuccess: async () => {
      setChanged(true)
      reset()
      await onChanged()
    },
    onError: (error) => {
      if (isApiError(error)) {
        const mapped = applyFieldErrors(error.problem, setError)
        if (!mapped) setFormError(problemMessage(error.problem))
      } else {
        setFormError(es.errors.unexpected)
      }
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    setChanged(false)
    mutation.mutate(values)
  })

  return (
    <section className={styles.section} aria-label={profileI18n.password.heading}>
      <Subtitle2 as="h2">{profileI18n.password.heading}</Subtitle2>
      <Text className={styles.sectionHint}>{profileI18n.password.description}</Text>
      {changed ? (
        <MessageBar intent="success">
          <MessageBarBody>{profileI18n.password.changed}</MessageBarBody>
        </MessageBar>
      ) : null}
      {formError ? (
        <MessageBar intent="error">
          <MessageBarBody>{formError}</MessageBarBody>
        </MessageBar>
      ) : null}
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <FormField
          control={control}
          name="current_password"
          label={profileI18n.password.currentLabel}
          required
          rules={{ required: profileI18n.validation.currentRequired }}
        >
          {(field, invalid) => (
            <Input
              {...field}
              type="password"
              autoComplete="current-password"
              aria-invalid={invalid}
            />
          )}
        </FormField>
        <FormField
          control={control}
          name="new_password"
          label={profileI18n.password.newLabel}
          required
          rules={{ required: profileI18n.validation.newRequired }}
        >
          {(field, invalid) => (
            <Input {...field} type="password" autoComplete="new-password" aria-invalid={invalid} />
          )}
        </FormField>
        <FormField
          control={control}
          name="confirm_password"
          label={profileI18n.password.confirmLabel}
          required
          rules={{
            required: profileI18n.validation.confirmRequired,
            validate: (value) =>
              value === watch("new_password") || profileI18n.validation.confirmMismatch,
          }}
        >
          {(field, invalid) => (
            <Input {...field} type="password" autoComplete="new-password" aria-invalid={invalid} />
          )}
        </FormField>
        <div className={styles.actions}>
          <Button
            appearance="primary"
            type="submit"
            disabled={mutation.isPending}
            icon={mutation.isPending ? <Spinner size="tiny" /> : undefined}
          >
            {profileI18n.password.submit}
          </Button>
        </div>
      </form>
    </section>
  )
}
